/**
 * Router + role guard for the producer cabinet.
 *
 * Served from the root of its own subdomain, provider.mostholding.org (D16), so there is no
 * router `basename` and Vite's `base` is `/`.
 *
 * A DATA router (`createBrowserRouter`) is required, not `<BrowserRouter>`: the unsaved-changes
 * guard on PRO-2 and PRO-4 uses `useBlocker`, which only exists on data routers.
 *
 * Route guards are UX ONLY. Authorization is enforced by the backend on every request (docs/06
 * §3) — this just avoids showing a producer a screen that would 403 anyway.
 */
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useRouteError,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { roleHome, VERIFY_EMAIL_PATH } from '@most/auth';
import { BlockedScreen, Shell, ShellSkeleton } from '@/components/Shell';
import { HomeScreen } from '@/screens/Home';
import { ProfileScreen } from '@/screens/Profile';
import { ProductsScreen } from '@/screens/Products';
import { ProductWizardScreen } from '@/screens/wizard/ProductWizard';
import { OrdersScreen } from '@/screens/Orders';
import { OrderSliceScreen } from '@/screens/OrderSlice';
import { NotificationsScreen } from '@/screens/Notifications';
import { useMe } from '@/shared/session';
import { EmptyState } from '@/components/ui';

/**
 * The producer gate.
 *
 *   • no session          → the 401 handler in `queryClient.ts` has already redirected to /login
 *   • wrong role          → bounce to that role's own app. We do NOT show a "wrong role" page
 *     with a link to /admin: never leak whether a route exists (docs/06 §3).
 *   • pending_email       → the verification prompt lives on the landing app
 *   • blocked             → neutral terminal screen
 *   • pending_approval    → the cabinet IS reachable (PRO-1, PRO-2); PRO-3/PRO-4 lock themselves
 *   • active              → everything
 */
function ProducerGuard() {
  const { data: me, isLoading, isError } = useMe();

  if (isLoading) return <ShellSkeleton />;

  // A failed /me that is NOT a 401 (the 401 already navigated away) means the API is unreachable.
  // Better a blank shell than a redirect loop.
  if (isError || !me) return <ShellSkeleton />;

  if (me.role !== 'producer') {
    window.location.assign(roleHome(me.role));
    return <ShellSkeleton />;
  }

  if (me.status === 'blocked') return <BlockedScreen />;

  if (me.status === 'pending_email') {
    window.location.assign(VERIFY_EMAIL_PATH);
    return <ShellSkeleton />;
  }

  return <Outlet />;
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-lg py-16">
      <EmptyState
        title="404"
        body={t('common.errorLoad')}
        action={
          <a
            href="/producer/"
            className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {t('nav.home')}
          </a>
        }
      />
    </div>
  );
}

/** Last-resort boundary: a render crash must not leave a blank white page. */
function RouteError() {
  const error = useRouteError();
  // eslint-disable-next-line no-console
  console.error(error);

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="max-w-md rounded-lg border border-red-200 bg-white p-8 text-center shadow-sm">
        <h1 className="text-lg font-semibold text-slate-900">Что-то пошло не так</h1>
        <p className="mt-2 text-sm text-slate-600">Обновите страницу или попробуйте позже.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Обновить
        </button>
      </div>
    </div>
  );
}

const router = createBrowserRouter(
  [
    {
      element: <ProducerGuard />,
      errorElement: <RouteError />,
      children: [
        {
          element: <Shell />,
          children: [
            { index: true, element: <HomeScreen /> }, // PRO-1
            { path: 'profile', element: <ProfileScreen /> }, // PRO-2
            { path: 'products', element: <ProductsScreen /> }, // PRO-3
            { path: 'products/new', element: <ProductWizardScreen /> }, // PRO-4
            { path: 'products/:id/edit', element: <ProductWizardScreen /> }, // PRO-4
            { path: 'orders', element: <OrdersScreen /> }, // PRO-5
            { path: 'orders/:number', element: <OrderSliceScreen /> }, // PRO-6
            { path: 'notifications', element: <NotificationsScreen /> }, // PRO-7
            { path: '404', element: <NotFound /> },
            { path: '*', element: <Navigate to="/404" replace /> },
          ],
        },
      ],
    },
  ],
  // No basename: since D16 this SPA is served from the root of provider.mostholding.org.
);

export function App() {
  return <RouterProvider router={router} />;
}
